import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  DragDotsIcon,
  TrashIcon,
} from "./filterPanelIcons";
import {
  FILTER_DIMENSION_KEYS,
  FILTER_VALUES_PAGE_SIZE,
  loadFilterDimensionValues,
  loadFilterDimensions,
  type FilterDimension,
  type FilterOption,
} from "../../../services/regulationFilterDimensionsService";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import {
  dimensionSelectionCount,
  emptyFilterSelection,
  filterSelectionsEqual,
  mergeFilterOptions,
  pinSelectedOptions,
  type FilterSelectionState,
} from "./regulationFilterDraft";
import { FilterCriteriaEditor } from "./FilterCriteriaEditor";

const FILTER_NAME_MAX_LENGTH = 30;
const ACTIVE_BG = "rgba(41, 94, 241, 0.08)";

export interface SavedFilterChoice {
  id: number;
  name: string;
  selection: FilterSelectionState;
}

export const SaveFilterNameDialog: React.FC<{
  open: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (name: string) => void | Promise<void>;
}> = ({ open, error, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const submit = () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    void Promise.resolve(onSave(name.trim())).finally(() => {
      savingRef.current = false;
      setSaving(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { sx: { zIndex: 1600 } },
        root: { sx: { zIndex: 1600 } },
      }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, color: "#0D0D0D" }}>
        Save filter
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0D0D0D", mb: 0.75 }}>
          Filter name{" "}
          <Box component="span" sx={{ color: "#FF3939" }}>
            *
          </Box>
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Enter a name for this filter"
          value={name}
          error={Boolean(error)}
          helperText={error ?? " "}
          onChange={(event) =>
            setName(event.target.value.slice(0, FILTER_NAME_MAX_LENGTH))
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#0D0D0D" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={submit}
          sx={{ textTransform: "none", bgcolor: "#295EF1" }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function SectionHeader({
  title,
  count,
}: {
  title: string;
  count: number;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 0.5,
        mb: 1,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{count}</Typography>
    </Box>
  );
}

function EmptySection({ message }: { message: string }) {
  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 2.5,
        textAlign: "center",
        color: "text.secondary",
        fontSize: 13,
      }}
    >
      {message}
    </Box>
  );
}

export const ManageSavedFiltersDialog: React.FC<{
  open: boolean;
  filters: SavedFilterChoice[];
  onClose: () => void;
  onSave: (
    id: number,
    name: string,
    selection: FilterSelectionState,
  ) => Promise<string | null>;
  onDelete: (id: number) => Promise<void>;
  onReorder: (orderedIds: number[]) => void;
}> = ({ open, filters, onClose, onSave, onDelete, onReorder }) => {
  const [activeId, setActiveId] = useState<number | null>(filters[0]?.id ?? null);
  const [name, setName] = useState(filters[0]?.name ?? "");
  const [draft, setDraft] = useState<FilterSelectionState>(
    filters[0]?.selection ?? emptyFilterSelection(),
  );
  const [criteriaKey, setCriteriaKey] = useState<string>(
    FILTER_DIMENSION_KEYS.favorite,
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [leaveAction, setLeaveAction] = useState<
    | { type: "close" }
    | { type: "switch"; id: number }
    | null
  >(null);
  const [filterToDelete, setFilterToDelete] = useState<SavedFilterChoice | null>(
    null,
  );
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState<FilterDimension[]>([]);
  const [pages, setPages] = useState<
    Record<string, { items: FilterOption[]; total: number; page: number }>
  >({});
  const [loadingMoreKey, setLoadingMoreKey] = useState<string | null>(null);
  const startedPages = useRef(new Set<string>());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      const nextDimensions = await loadFilterDimensions();
      if (!cancelled) setDimensions(nextDimensions);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || dimensions.length === 0) return;
    const dimension =
      dimensions.find((item) => item.key === criteriaKey) ?? dimensions[0];
    if (!dimension) return;
    const token = `${dimension.key}:1`;
    if (startedPages.current.has(token)) return;
    startedPages.current.add(token);
    let cancelled = false;
    void (async () => {
      const result = await loadFilterDimensionValues(dimension, {
        page: 1,
        pageSize: FILTER_VALUES_PAGE_SIZE,
      });
      if (cancelled) {
        startedPages.current.delete(token);
        return;
      }
      setPages((current) => ({
        ...current,
        [dimension.key]: {
          items: result.items,
          total: result.total,
          page: result.page,
        },
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dimensions, criteriaKey]);

  const optionsByKey = useMemo(() => {
    const next: Record<string, FilterOption[]> = {};
    for (const dimension of dimensions) {
      const loaded = pages[dimension.key]?.items ?? [];
      const selected = draft.selections[dimension.key] ?? [];
      next[dimension.key] = pinSelectedOptions(loaded, selected, "", {
        selection: dimension.selection,
        preserveSelectionOrder: dimension.key === FILTER_DIMENSION_KEYS.tags,
        includeMissing: true,
      });
    }
    return next;
  }, [dimensions, pages, draft.selections]);

  const loadMoreActiveValues = () => {
    const dimension =
      dimensions.find((item) => item.key === criteriaKey) ?? dimensions[0];
    const page = dimension ? pages[dimension.key] : undefined;
    if (!dimension || !page || page.items.length >= page.total) return;
    const nextPage = page.page + 1;
    const token = `${dimension.key}:${nextPage}`;
    if (startedPages.current.has(token)) return;
    startedPages.current.add(token);
    setLoadingMoreKey(dimension.key);
    void loadFilterDimensionValues(dimension, {
      page: nextPage,
      pageSize: FILTER_VALUES_PAGE_SIZE,
    }).then((result) => {
      setPages((current) => {
        const previous = current[dimension.key];
        return {
          ...current,
          [dimension.key]: {
            items: mergeFilterOptions(previous?.items ?? [], result.items),
            total: result.total,
            page: result.page,
          },
        };
      });
      setLoadingMoreKey((current) => (current === dimension.key ? null : current));
    });
  };

  const activeDimension =
    dimensions.find((dimension) => dimension.key === criteriaKey) ??
    dimensions[0];
  const activeValuesReady =
    activeDimension != null && pages[activeDimension.key] != null;

  const active = filters.find((filter) => filter.id === activeId) ?? null;
  const dirty =
    active != null &&
    (name.trim() !== active.name ||
      !filterSelectionsEqual(draft, active.selection));

  const loadFilter = (filter: SavedFilterChoice) => {
    setActiveId(filter.id);
    setName(filter.name);
    setDraft(filter.selection);
    setNameError(null);
    const firstSelected =
      dimensions.find(
        (dimension) => dimensionSelectionCount(dimension.key, filter.selection) > 0,
      )?.key ??
      dimensions[0]?.key ??
      FILTER_DIMENSION_KEYS.favorite;
    setCriteriaKey(firstSelected);
  };

  const requestSwitch = (filter: SavedFilterChoice) => {
    if (filter.id === activeId) return;
    if (dirty) {
      setLeaveAction({ type: "switch", id: filter.id });
      return;
    }
    loadFilter(filter);
  };

  const requestClose = () => {
    if (dirty) {
      setLeaveAction({ type: "close" });
      return;
    }
    onClose();
  };

  const confirmLeave = () => {
    const action = leaveAction;
    setLeaveAction(null);
    if (!action) return;
    if (action.type === "close") {
      onClose();
      return;
    }
    const next = filters.find((filter) => filter.id === action.id);
    if (next) loadFilter(next);
  };

  const handleSave = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const error = await onSave(active.id, name.trim(), draft);
      setNameError(error);
    } finally {
      setSaving(false);
    }
  };

  const moveFilter = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= filters.length) return;
    const next = [...filters];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onReorder(next.map((filter) => filter.id));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(requestClose)}
        slotProps={{
          root: { sx: { zIndex: 1500 } },
          paper: {
            sx: {
              width: 880,
              maxWidth: "calc(100% - 32px)",
              height: 560,
              maxHeight: "calc(100% - 48px)",
              borderRadius: "8px",
              boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 16,
            fontWeight: 600,
            color: "#0D0D0D",
            px: "20px",
            py: "16px",
            borderBottom: "1px solid #F0F1F3",
            m: 0,
          }}
        >
          Manage saved filters
          <IconButton
            aria-label="Close"
            onClick={requestClose}
            disableRipple
            sx={{
              width: 24,
              height: 24,
              color: "#6A7282",
              fontSize: 15,
              lineHeight: 1,
              bgcolor: "transparent",
              "&:hover": { bgcolor: "transparent", color: "#0D0D0D" },
            }}
          >
            ✕
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", gap: 0, p: 0, overflow: "hidden", flex: 1 }}
        >
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              overflowY: "auto",
              px: 2,
              py: 2,
              borderRight: "1px solid #F0F1F3",
            }}
          >
            <SectionHeader title="Saved filters" count={filters.length} />
            {filters.length === 0 ? (
              <EmptySection message="No saved filters." />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {filters.map((filter, index) => {
                  const selected = filter.id === activeId;
                  return (
                    <Box
                      key={filter.id}
                      draggable
                      onDragStart={() => setDraggingIndex(index)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (draggingIndex != null) {
                          moveFilter(draggingIndex, index);
                        }
                        setDraggingIndex(null);
                      }}
                      onClick={() => requestSwitch(filter)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.85,
                        borderRadius: 1,
                        cursor: "pointer",
                        position: "relative",
                        bgcolor: selected ? ACTIVE_BG : "transparent",
                        "&::before": selected
                          ? {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3,
                              bgcolor: "#295EF1",
                              borderRadius: "3px 0 0 3px",
                            }
                          : undefined,
                        "&:hover": {
                          bgcolor: selected ? ACTIVE_BG : "action.hover",
                        },
                      }}
                    >
                      <DragDotsIcon
                        sx={{ fontSize: 12, color: "#A6A6A6", cursor: "grab" }}
                      />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: 14,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {filter.name}
                      </Typography>
                      <Tooltip title="Delete filter">
                        <IconButton
                          aria-label="Delete filter"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setFilterToDelete(filter);
                          }}
                          sx={{
                            color: "#A6A6A6",
                            "&:hover": { color: "#D4183D", bgcolor: "transparent" },
                          }}
                        >
                          <TrashIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, overflowY: "auto", px: 2.5, py: 2 }}>
            {active ? (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75, color: "#0D0D0D" }}>
                  Filter name{" "}
                  <Box component="span" sx={{ color: "#E30000" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={name}
                  error={Boolean(nameError)}
                  helperText={nameError ?? " "}
                  onChange={(event) => {
                    setName(event.target.value.slice(0, FILTER_NAME_MAX_LENGTH));
                    setNameError(null);
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "3.6px",
                      fontSize: 13,
                    },
                  }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75, color: "#0D0D0D" }}>
                  Filter criteria
                </Typography>
                <Box
                  sx={{
                    border: "1px solid #F0F1F3",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                {dimensions.length > 0 ? (
                  <FilterCriteriaEditor
                    dimensions={dimensions}
                    draft={draft}
                    activeKey={criteriaKey}
                    onActiveKeyChange={setCriteriaKey}
                    onDraftChange={setDraft}
                    optionsByKey={optionsByKey}
                    onReachEnd={loadMoreActiveValues}
                    valuesLoading={!activeValuesReady}
                    loadingMore={loadingMoreKey === activeDimension?.key}
                    maxHeight={320}
                  />
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={22} />
                  </Box>
                )}
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                Select a saved filter to edit its name and criteria.
              </Typography>
            )}
          </Box>
        </DialogContent>
        {dirty && (
          <DialogActions
            sx={{
              px: "20px",
              py: 1.5,
              borderTop: "1px solid #F0F1F3",
            }}
          >
            <Button
              variant="contained"
              disabled={saving}
              onClick={() => void handleSave()}
              sx={{ textTransform: "none", bgcolor: "#295EF1" }}
            >
              Save changes
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={leaveAction != null}
        onClose={withoutBackdropClose(() => setLeaveAction(null))}
        slotProps={{ root: { sx: { zIndex: 1700 } } }}
      >
        <DialogTitle sx={{ fontSize: 18, color: "#101828", pr: 6 }}>
          Leave without saving?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: "#172B4D" }}>
            You have unsaved changes to this saved filter. If you leave now, your
            changes will be discarded.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setLeaveAction(null)}
            sx={{ textTransform: "none", color: "#295EF1" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmLeave}
            sx={{ textTransform: "none", bgcolor: "#295EF1" }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={filterToDelete != null}
        onClose={withoutBackdropClose(() => setFilterToDelete(null))}
        slotProps={{ root: { sx: { zIndex: 1700 } } }}
      >
        <DialogTitle sx={{ fontSize: 18, color: "#101828" }}>
          Confirm Delete?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: "#172B4D" }}>
            {`Are you sure you want to delete "${filterToDelete?.name ?? ""}"? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setFilterToDelete(null)}
            sx={{ textTransform: "none", color: "#295EF1" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#295EF1" }}
            onClick={() => {
              if (!filterToDelete) return;
              const deletedId = filterToDelete.id;
              setFilterToDelete(null);
              void onDelete(deletedId).then(() => {
                if (activeId === deletedId) {
                  const remaining = filters.filter(
                    (filter) => filter.id !== deletedId,
                  );
                  if (remaining[0]) loadFilter(remaining[0]);
                  else {
                    setActiveId(null);
                    setName("");
                    setDraft(emptyFilterSelection());
                  }
                }
              });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
